import { useEffect } from 'react'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { interactiveLoginRequest, loginRequest } from '../azureAuthConfig'
import userStoredData from '../userStoredData'
import { completeAzureLogin, startAzureButtonLogin, startSilentAzureLogin } from './azureLogin'
import { azureLoginFailureMessage, isNoUserError, NO_USER_NOTIFICATION_DURATION } from './azureLoginFailure'

const warnIncomplete = e => {
  console.warn('Azure sign-in did not complete', e)
}

// The Azure button's behaviour: the silent login after a sign-in and the click handler (see
// azureLogin.js). `flight` must outlive the button: Login renders nothing while a credentials login or
// the profile loads, which unmounts the button, and a remount re-runs the isAuthenticated effect — with
// a flight of its own it would start a second silent login while the first is still completing.
// `errorNotification` and `t` report a token exchange the platform refused.
export default function useAzureLogin({ flight, fetchApiKeyWithOauthToken, fetchProfileAndRedirect, errorNotification, t, azureOnly = false }) {
  const { instance, accounts } = useMsal()
  const isAuthenticated = useIsAuthenticated()

  // The token-to-session exchange, whatever produced the token. Its rejection is the one failure the
  // person can act on — the browser signed them in as an account the platform does not know (#1426) —
  // so it is reported from here, which covers the click and the silent completion alike (the sign-in
  // popup is finished by the silent request). MSAL's own failures (popup closed or blocked) never get
  // this far. An account the platform accepted becomes MSAL's active account: the next silent login in
  // this tab asks for its token, not for whichever account happened to be cached first.
  const complete = ({ response, account }) => {
    const signedIn = get(response, 'account') || account
    return completeAzureLogin({ response, account, fetchApiKeyWithOauthToken, fetchProfileAndRedirect, session: userStoredData }).then(
      () => {
        instance.setActiveAccount(signedIn)
      },
      error => {
        const noUser = isNoUserError(error)
        // console.error reaches Sentry (captureConsole); no identity in the line
        if (noUser) console.error('Azure login refused by the platform: no user for the signed-in account')
        errorNotification(azureLoginFailureMessage({ error, account: signedIn, t }), noUser ? NO_USER_NOTIFICATION_DURATION : undefined)
        throw error
      }
    )
  }

  const azureLoginHandler = () => {
    // Interactive, so with the account picker: never the account the browser happens to be signed in as.
    startAzureButtonLogin({ instance, accounts, hasSession: !!userStoredData.API_KEY, request: interactiveLoginRequest, complete, flight }).catch(
      warnIncomplete
    )
  }

  useEffect(() => {
    // No user gesture here: only the sign-in popup for a browser with no cached account, as before.
    if (azureOnly && !userStoredData.API_KEY && isEmpty(accounts)) azureLoginHandler()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [azureOnly])

  useEffect(() => {
    // An attempt already in flight (started before this mount) finishes the login itself.
    if (isAuthenticated && !flight.isInFlight()) {
      startSilentAzureLogin({ instance, account: instance.getActiveAccount() || accounts[0], request: loginRequest, complete, flight }).catch(warnIncomplete)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  return azureLoginHandler
}
